import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, API_BASE } from "../lib/api";
import { formatCurrency, formatDate, STATUS_STYLES, STATUSES, warrantyStatus, PLATFORMS } from "../lib/constants";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Search, Download, Plus, MoreHorizontal, Eye, Pencil, Trash2, ShieldCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export default function CustomersPage() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [platform, setPlatform] = useState("all");
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = () => {
    setLoading(true);
    api.get("/customers").then((r) => setRows(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (status !== "all" && r.status !== status) return false;
      if (platform !== "all" && r.platform !== platform) return false;
      if (q) {
        const s = q.toLowerCase();
        return [r.first_name, r.last_name, r.email, r.phone].some((v) => (v || "").toLowerCase().includes(s));
      }
      return true;
    });
  }, [rows, q, status, platform]);

  const del = async (id) => {
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Müşteri silindi");
      load();
    } catch (e) {
      toast.error("Silinemedi");
    }
  };

  const exportCsv = () => {
    // Trigger download using cookies
    window.location.href = `${API_BASE}/customers/export/csv`;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Kayıtlar</div>
          <div className="text-2xl font-display font-semibold tracking-tight mt-1">Tüm Müşteriler ({rows.length})</div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn" className="rounded-full">
            <Download className="h-4 w-4 mr-2" /> CSV Dışa Aktar
          </Button>
          <Button asChild data-testid="new-customer-btn" className="rounded-full">
            <Link to="/customers/new"><Plus className="h-4 w-4 mr-2" /> Yeni Müşteri</Link>
          </Button>
        </div>
      </div>

      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="İsim, e-posta veya telefonda ara..."
              className="pl-9"
              data-testid="customer-search"
            />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="md:w-48" data-testid="filter-status"><SelectValue placeholder="Durum" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Durumlar</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={platform} onValueChange={setPlatform}>
            <SelectTrigger className="md:w-48" data-testid="filter-platform"><SelectValue placeholder="Platform" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Platformlar</SelectItem>
              {PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="customers-table">
            <TableHeader>
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                <TableHead className="whitespace-nowrap">Müşteri</TableHead>
                <TableHead className="whitespace-nowrap">İletişim</TableHead>
                <TableHead className="whitespace-nowrap">Platform</TableHead>
                <TableHead className="whitespace-nowrap">Paket</TableHead>
                <TableHead className="whitespace-nowrap">Fiyat</TableHead>
                <TableHead className="whitespace-nowrap">Teslimat</TableHead>
                <TableHead className="whitespace-nowrap">Garanti</TableHead>
                <TableHead className="whitespace-nowrap">Durum</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center py-16 text-muted-foreground">Yükleniyor...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-16 text-muted-foreground">Henüz kayıt yok. İlk müşterini ekle.</TableCell></TableRow>
              ) : filtered.map((c) => {
                const w = warrantyStatus(c.warranty_end_date);
                const s = STATUS_STYLES[c.status];
                return (
                  <TableRow key={c.id} className="hover:bg-muted/40 cursor-pointer" onClick={() => nav(`/customers/${c.id}`)} data-testid={`customer-row-${c.id}`}>
                    <TableCell className="font-medium">
                      <div>{c.first_name} {c.last_name}</div>
                      <div className="text-xs text-muted-foreground">{c.country}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{c.email}</div>
                      <div className="text-xs text-muted-foreground">{c.phone}</div>
                    </TableCell>
                    <TableCell>{c.platform}</TableCell>
                    <TableCell>{c.package}</TableCell>
                    <TableCell className="font-mono text-sm">{formatCurrency(c.price)}</TableCell>
                    <TableCell className="text-sm">{formatDate(c.delivery_date)}</TableCell>
                    <TableCell>
                      {w.active ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-green-500/10 text-green-600 dark:text-green-400 px-2.5 py-1 text-xs font-semibold">
                          <ShieldCheck className="h-3 w-3" /> {w.daysLeft}g kaldı
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-red-500/10 text-red-600 dark:text-red-400 px-2.5 py-1 text-xs font-semibold">
                          <ShieldAlert className="h-3 w-3" /> Süresi doldu
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center rounded-full ${s.bg} ${s.text} px-2.5 py-1 text-[10px] font-semibold tracking-wider`}>
                        {c.status}
                      </span>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`customer-actions-${c.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => nav(`/customers/${c.id}`)}>
                            <Eye className="h-4 w-4 mr-2" /> Görüntüle
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => nav(`/customers/${c.id}/edit`)}>
                            <Pencil className="h-4 w-4 mr-2" /> Düzenle
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-red-600 dark:text-red-400">
                                <Trash2 className="h-4 w-4 mr-2" /> Sil
                              </DropdownMenuItem>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Müşteri silinsin mi?</AlertDialogTitle>
                                <AlertDialogDescription>Bu işlem geri alınamaz. {c.first_name} {c.last_name} kaydı kalıcı olarak silinecek.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>İptal</AlertDialogCancel>
                                <AlertDialogAction onClick={() => del(c.id)} data-testid={`confirm-delete-${c.id}`}>Sil</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
